import React, { useEffect, useRef, useState } from 'react'
import './HeroSection.css'

const ROLES = [
  'Développeur Web Full-Stack',
  'Data Analyst & IA',
  'Développeur Logiciel',
  'Machine Learning Engineer',
]

function useTypingEffect(words, speed = 80, pause = 2000) {
  const [display, setDisplay] = useState('')
  const [wordIdx, setWordIdx] = useState(0)
  const [charIdx, setCharIdx] = useState(0)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    const current = words[wordIdx]
    let timeout

    if (!deleting && charIdx < current.length) {
      timeout = setTimeout(() => setCharIdx(c => c + 1), speed)
    } else if (!deleting && charIdx === current.length) {
      timeout = setTimeout(() => setDeleting(true), pause)
    } else if (deleting && charIdx > 0) {
      timeout = setTimeout(() => setCharIdx(c => c - 1), speed / 2)
    } else if (deleting && charIdx === 0) {
      setDeleting(false)
      setWordIdx(i => (i + 1) % words.length)
    }

    setDisplay(current.slice(0, charIdx))
    return () => clearTimeout(timeout)
  }, [charIdx, deleting, wordIdx, words, speed, pause])

  return display
}

export default function HeroSection() {
  const [visible, setVisible] = useState(false)
  const orbRef = useRef(null)
  const role = useTypingEffect(ROLES)

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    const onMove = (e) => {
      if (!orbRef.current) return
      const x = (e.clientX / window.innerWidth - 0.5) * 30
      const y = (e.clientY / window.innerHeight - 0.5) * 30
      orbRef.current.style.transform = `translate(${x}px, ${y}px)`
    }
    window.addEventListener('mousemove', onMove)
    return () => window.removeEventListener('mousemove', onMove)
  }, [])

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })

  return (
    <section id="hero" className="hero-section section" aria-label="Accueil">
      {/* Ambient orbs */}
      <div className="hero-orbs" ref={orbRef}>
        <div className="hero-orb hero-orb-1" />
        <div className="hero-orb hero-orb-2" />
      </div>

      <div className={`hero-content ${visible ? 'visible' : ''}`}>
        {/* Eyebrow */}
        <div className="hero-eyebrow">
          <span className="dot-pulse" />
          <span>Disponible pour alternance 2026–2028</span>
        </div>

        {/* Name */}
        <h1 className="hero-name">
          <span className="hero-name-line">Quentin</span>
          <span className="hero-name-line hero-name-accent gradient-text">Ott</span>
        </h1>

        {/* Typing role */}
        <div className="hero-role" aria-live="polite">
          <span className="hero-role-text">{role}</span>
          <span className="hero-cursor" aria-hidden="true">|</span>
        </div>

        {/* Description */}
        <p className="hero-desc">
          Étudiant en <strong>Bachelor 3 IA & Data</strong> à Ynov Campus Nanterre,
          je conçois des expériences web, des outils data et des solutions IA.
          Passionné par la technologie, je cherche une alternance en <strong>Master Expert Systèmes Connectés IA & IoT</strong>.
        </p>

        {/* CTA */}
        <div className="hero-actions">
          <button className="btn-primary" onClick={() => scrollTo('projects')}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 12l4-4 3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Voir mes projets
          </button>
          <a href="/CV_Quentin_Ott.pdf" download className="btn-secondary">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 2v9M5 8l3 3 3-3M3 13h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Télécharger CV
          </a>
        </div>

        {/* Social links */}
        <div className="hero-socials">
          <a href="mailto:quentin.ott2511@gmail.com" className="hero-social" aria-label="Email">
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M2 7l8 5 8-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </a>
          <a href="https://github.com/QuentinOtt" target="_blank" rel="noopener noreferrer" className="hero-social" aria-label="GitHub">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/>
            </svg>
          </a>
          <a href="https://linkedin.com/in/quentin-ott" target="_blank" rel="noopener noreferrer" className="hero-social" aria-label="LinkedIn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </a>
          <a href="tel:0601862082" className="hero-social" aria-label="Téléphone">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1L6.6 10.8z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </a>
        </div>

        {/* Scroll indicator */}
        <button className="hero-scroll" onClick={() => scrollTo('about')} aria-label="Défiler vers le bas">
          <span className="scroll-label">Défiler</span>
          <div className="scroll-line">
            <div className="scroll-dot" />
          </div>
        </button>
      </div>

      {/* Floating badges */}
      <div className={`hero-badges ${visible ? 'visible' : ''}`} aria-hidden="true">
        <div className="hero-badge badge-1">
          <span className="badge-icon">🎓</span>
          <div>
            <div className="badge-title">Bachelor 3</div>
            <div className="badge-sub">IA & Data — Ynov</div>
          </div>
        </div>
        <div className="hero-badge badge-2">
          <span className="badge-icon">💼</span>
          <div>
            <div className="badge-title">Freelance</div>
            <div className="badge-sub">Depuis 2024</div>
          </div>
        </div>
        <div className="hero-badge badge-3">
          <span className="badge-icon">🌍</span>
          <div>
            <div className="badge-title">Langues</div>
            <div className="badge-sub">FR · EN · ES</div>
          </div>
        </div>
      </div>
    </section>
  )
}
